import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { PaperPlaneTilt, ArrowClockwise, ChatCircleDots, ClipboardText, Paperclip, X, FilePdf, FileText, Play } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import LogScoreModal from "@/components/LogScoreModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Build a fully qualified URL for an attachment served through the backend proxy.
const attachmentSrc = (url) => (url && url.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);

const humanSize = (bytes) => {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Reusable 1:1 messages thread between a coach and their client.
 * Works from either perspective — pass linkId to identify the thread.
 * Polls every 8s while mounted; also refreshes right after sending.
 *
 * When `enableTestChips` is true (athlete-side chat), each incoming coach message
 * is scanned against the athlete's test library. Any test whose name appears in
 * the message (whole-word, case-insensitive) surfaces a LOG SCORE chip that opens
 * a pre-filled log modal.
 */
export default function MessagesThread({ linkId, currentUserId, height = "440px", enableTestChips = false, onScoreLogged }) {
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tests, setTests] = useState([]);
  const [logOpen, setLogOpen] = useState(null); // test object
  const [pendingFile, setPendingFile] = useState(null);  // File object staged for upload
  const [pendingPreview, setPendingPreview] = useState(null); // object URL for image preview
  const [uploadingPct, setUploadingPct] = useState(0);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/threads/${linkId}`);
      setThread(data);
      setTimeout(scrollToBottom, 50);
    } catch (e) {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [linkId]);

  useEffect(() => {
    if (!linkId) return;
    load();
    pollRef.current = setInterval(load, 8000);
    return () => clearInterval(pollRef.current);
  }, [linkId, load]);

  // One-off fetch of the athlete's visible test library for chip matching
  useEffect(() => {
    if (!enableTestChips) return;
    api.get("/athlete/test-library")
      .then(({ data }) => setTests(data || []))
      .catch(() => {});
  }, [enableTestChips]);

  // Clean up any object URL when the pending file changes/clears
  useEffect(() => {
    return () => { if (pendingPreview) URL.revokeObjectURL(pendingPreview); };
  }, [pendingPreview]);

  // Build regex-safe search patterns once; word-boundary case-insensitive match.
  const testMatchers = useMemo(() => {
    if (!enableTestChips || !tests.length) return [];
    return tests
      .filter((t) => (t.name || "").trim().length >= 3)
      .map((t) => {
        const escaped = t.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return { test: t, re: new RegExp(`\\b${escaped}\\b`, "i") };
      });
  }, [enableTestChips, tests]);

  const findTestsInText = useCallback((text) => {
    if (!text || !testMatchers.length) return [];
    const hits = [];
    const seen = new Set();
    for (const m of testMatchers) {
      if (m.re.test(text) && !seen.has(m.test.test_id)) {
        hits.push(m.test);
        seen.add(m.test.test_id);
        if (hits.length >= 3) break; // cap chips per message
      }
    }
    return hits;
  }, [testMatchers]);

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Client-side pre-flight to give fast feedback (backend still enforces).
    const mime = f.type || "application/octet-stream";
    let limitMB = 10;
    if (mime.startsWith("video/")) limitMB = 100;
    else if (mime === "application/pdf") limitMB = 25;
    if (f.size > limitMB * 1024 * 1024) {
      toast.error(`File too large (max ${limitMB} MB)`);
      e.target.value = "";
      return;
    }
    setPendingFile(f);
    if (mime.startsWith("image/")) {
      setPendingPreview(URL.createObjectURL(f));
    } else {
      setPendingPreview(null);
    }
    // Clear the input value so the same file can be re-picked after removal.
    e.target.value = "";
  };

  const clearPending = () => {
    setPendingFile(null);
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingPreview(null);
    setUploadingPct(0);
  };

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if ((!t && !pendingFile) || sending) return;
    setSending(true);
    try {
      let attachment = null;
      if (pendingFile) {
        const fd = new FormData();
        fd.append("file", pendingFile);
        const { data } = await api.post(
          `/messages/threads/${linkId}/attachments/upload`,
          fd,
          {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (evt) => {
              if (evt.total) setUploadingPct(Math.round((evt.loaded / evt.total) * 100));
            },
          },
        );
        attachment = data;
      }
      await api.post(`/messages/threads/${linkId}`, { text: t, attachment });
      setText("");
      clearPending();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
      setUploadingPct(0);
    }
  };

  const messages = thread?.messages || [];
  const other = thread?.other;
  const inactive = thread && thread.status !== "active";

  const renderAttachment = (att, mine) => {
    if (!att) return null;
    const src = attachmentSrc(att.url);
    const captionCls = mine ? "text-white/70" : "text-text-secondary";
    if (att.kind === "image") {
      return (
        <a href={src} target="_blank" rel="noopener noreferrer" className="block mt-1" data-testid="message-attachment-image">
          <img src={src} alt={att.name || "image"} className="max-w-full max-h-64 object-contain border border-white/10" loading="lazy" />
        </a>
      );
    }
    if (att.kind === "video") {
      return (
        <video src={src} controls className="mt-1 max-w-full max-h-64 border border-white/10" data-testid="message-attachment-video" />
      );
    }
    const Icon = att.kind === "pdf" ? FilePdf : FileText;
    return (
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        download={att.name}
        className={`mt-1 flex items-center gap-2 border border-white/10 px-2 py-1.5 hover:border-brand-accent transition-colors ${captionCls}`}
        data-testid="message-attachment-file"
      >
        <Icon size={18} weight="duotone" className="text-brand-accent shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-xs truncate">{att.name || "attachment"}</div>
          <div className="text-[10px] tabular-nums opacity-70">{humanSize(att.size)}</div>
        </div>
      </a>
    );
  };

  return (
    <div className="border border-white/10 bg-surface-DEFAULT" data-testid="messages-thread">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <ChatCircleDots size={16} weight="duotone" className="text-brand-accent" />
          <span className="label-tiny">MESSAGES{other?.name ? ` · ${other.name.toUpperCase()}` : ""}</span>
        </div>
        <button
          onClick={load}
          className="text-text-secondary hover:text-white transition-colors"
          data-testid="messages-refresh-button"
          aria-label="Refresh messages"
        >
          <ArrowClockwise size={14} />
        </button>
      </div>

      <div
        ref={scrollRef}
        className="overflow-y-auto p-4 space-y-3"
        style={{ height }}
        data-testid="messages-scroll-area"
      >
        {loading ? (
          <div className="text-center text-xs text-text-secondary py-8 label-tiny">LOADING...</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-sm text-text-secondary py-8" data-testid="messages-empty">
            No messages yet. Send the first one below.
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === currentUserId;
            const chipTests = (!mine && enableTestChips) ? findTestsInText(m.text) : [];
            return (
              <div
                key={m.message_id}
                className={`flex flex-col ${mine ? "items-end" : "items-start"}`}
                data-testid={`message-${m.message_id}`}
              >
                <div
                  className={`max-w-[80%] px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                    mine
                      ? "bg-brand-primary text-white"
                      : "bg-surface-elevated border border-white/10 text-white"
                  }`}
                >
                  {m.text ? <div>{m.text}</div> : null}
                  {renderAttachment(m.attachment, mine)}
                  <div
                    className={`mt-1 text-[10px] tabular-nums ${
                      mine ? "text-white/70" : "text-text-secondary"
                    }`}
                  >
                    {new Date(m.created_at).toLocaleString([], {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                    {mine && m.read_at ? " · READ" : ""}
                  </div>
                </div>
                {chipTests.length > 0 && (
                  <div
                    className="mt-1.5 flex flex-wrap gap-1.5 max-w-[80%]"
                    data-testid={`message-chips-${m.message_id}`}
                  >
                    {chipTests.map((t) => (
                      <button
                        key={t.test_id}
                        onClick={() => setLogOpen(t)}
                        className="inline-flex items-center gap-1 border border-brand-accent/50 bg-brand-accent/10 text-brand-accent hover:bg-brand-accent hover:text-surface-base px-2.5 py-1 text-[10px] label-tiny tracking-widest transition-colors"
                        data-testid={`log-score-chip-${t.test_id}`}
                      >
                        <ClipboardText size={11} weight="bold" /> LOG SCORE · {t.name.toUpperCase()}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {inactive ? (
        <div className="border-t border-white/10 px-4 py-3 text-xs text-text-secondary label-tiny" data-testid="messages-inactive">
          THIS COACHING RELATIONSHIP IS NO LONGER ACTIVE
        </div>
      ) : (
        <form onSubmit={send} className="border-t border-white/10 p-3 space-y-2">
          {pendingFile && (
            <div className="flex items-center gap-3 border border-white/10 bg-surface-elevated px-2 py-1.5" data-testid="messages-pending-attachment">
              {pendingPreview ? (
                <img src={pendingPreview} alt="" className="w-12 h-12 object-cover border border-white/10" />
              ) : (
                <div className="w-12 h-12 flex items-center justify-center border border-white/10 text-brand-accent">
                  {pendingFile.type?.startsWith("video/") ? <Play size={18} weight="fill" /> : pendingFile.type === "application/pdf" ? <FilePdf size={22} weight="duotone" /> : <FileText size={22} weight="duotone" />}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs truncate text-white">{pendingFile.name}</div>
                <div className="text-[10px] label-tiny text-text-secondary tabular-nums">
                  {humanSize(pendingFile.size)}{sending && uploadingPct > 0 ? ` · UPLOADING ${uploadingPct}%` : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={clearPending}
                className="text-text-secondary hover:text-white transition-colors p-1"
                aria-label="Remove attachment"
                data-testid="messages-remove-attachment"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*,application/pdf"
              className="hidden"
              onChange={onFilePicked}
              data-testid="messages-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              className="w-10 h-10 shrink-0 flex items-center justify-center border border-white/10 hover:border-brand-accent hover:text-brand-accent transition-colors text-text-secondary disabled:opacity-50"
              aria-label="Attach file"
              data-testid="messages-attach-button"
            >
              <Paperclip size={16} weight="bold" />
            </button>
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={pendingFile ? "Add a caption (optional)…" : "Type a message..."}
              className="flex-1 bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              maxLength={4000}
              data-testid="messages-input"
            />
            <button
              type="submit"
              disabled={sending || (!text.trim() && !pendingFile)}
              className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest disabled:opacity-50 flex items-center gap-2"
              data-testid="messages-send-button"
            >
              <PaperPlaneTilt size={12} weight="bold" />
              {sending ? "SENDING..." : "SEND"}
            </button>
          </div>
        </form>
      )}

      {logOpen && (
        <LogScoreModal
          test={logOpen}
          onClose={() => setLogOpen(null)}
          onSaved={() => {
            setLogOpen(null);
            onScoreLogged?.();
          }}
        />
      )}
    </div>
  );
}
